'use client';

import { useId, useState } from 'react';
import type { FormEvent } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiCheckLine } from '@remixicon/react';
import { useLocale, useTranslations } from 'next-intl';
import { toLocale, type DemoRequestAck } from '@mentorivo/shared';

import { AuthField, FieldError, FormAlert, SubmitButton } from '@/components/auth-parts';
import { ApiError, api } from '@/lib/api';
import { COMPANY, companyPhoneHref } from '@/lib/company';
import { dur, ease, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

/**
 * /contact — sayfanın TAMAMI. Solda başlık ve iletişim bilgileri, sağda form.
 *
 * ── Sayfa neden tek bölüm ─────────────────────────────────────────────────
 *
 * Önceki sürümde üç bölüm vardı (başlık bloğu → form + yan sütun → kapanış
 * CTA'sı) ve form kıvrımın altında kalıyordu. Kullanıcı kararı (17 Eyl 2026):
 * "çok basit yap, heroda sağda form solda başlık ve iletişim bilgileri".
 * Sayfa artık header + bu bölüm + footer'dan ibaret; yan sütundaki üç
 * yönlendirme ve CTA şeridi kaldırıldı.
 *
 * Form alanları da azaldı: telefon ve aylık harcama seçici çıktı. Dört alan
 * (ad, e-posta, şirket, mesaj) hero yüksekliğine sığıyor; altı alan sığmıyor.
 * İkisi de sözleşmede DURUYOR (`demoRequestInputSchema`) — geri istenirse
 * yalnız bu dosyaya ve çeviriye satır eklenir.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 YENİ BİR UÇ YOK: form `POST /sales/demo-requests`e gidiyor
 * ═══════════════════════════════════════════════════════════════════════════
 * O uç zaten herkese açık (`@Public()`), dar hız sınırlı (5/dk), bal küplü ve
 * 202 dönüyor; arkasındaki `demo_requests` tablosunu yönetim panelindeki
 * "gelen talepler" ekranı okuyor. İkinci bir iletişim tablosu, aynı işi yapan
 * ikinci bir kuyruk ve er geç unutulan ikinci bir ekran demekti.
 *
 * ── İletişim bilgileri: şirket künyesi ────────────────────────────────────
 *
 * 14 Eki 2026'ya kadar e-posta ve telefon satırları BOŞTU ve basılmıyordu:
 * mentorivo.com'un posta kutusu yok (MX Yandex'te) ve numara belli değildi.
 * O gün iyzico üye iş yeri incelemesi satıcının kimliğini ve iletişim
 * bilgisini sitede açıkça istedi; değerler artık yasal metinlerin satıcı
 * tablosuyla AYNI şirket künyesinden (`lib/company.ts`) geliyor — unvan,
 * adres, vergi dairesi/no, info@mekjoy.com ve +90 850 840 79 00. Sözleşmede
 * yazan satıcı ile iletişim sayfasındaki satıcı aynı kişi olmak zorunda.
 *
 * Değerler çeviriden DEĞİL: 11 dil DeepL çıktısı ve onlarda e-posta/telefon
 * boş kalmıştı — çeviriden okunsaydı o dillerde satır yine görünmezdi.
 * Çeviride yalnız etiketler ve çalışma saati var. Değeri boş olan satır hâlâ
 * basılmıyor (çalışma saati çevirisi boş bırakılırsa).
 *
 * ── Bal küpü ──────────────────────────────────────────────────────────────
 *
 * `company_website` ekranda görünmüyor ama DOM'da: sunucu onu dolu görürse
 * 202 dönüp satırı YAZMIYOR. `display:none` değil — bazı botlar görünmeyen
 * alanı atlıyor; kutu ekran dışına alınıyor ve sekme sırasından çıkıyor.
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 *
 * Bölüm ekranın tepesinde açılıyor; `whileInView` beklemesi anlamsız,
 * `animate` ile doğrudan oynuyor (aynı gerekçe: components/page-head.tsx).
 * Sıra okuma sırası: etiket → başlık → cümle → künye → form. Blur yalnızca
 * başlıkta. Gönderim bitince form yerini başarı kartına BIRAKIYOR
 * (`mode="wait"`): hareket "o yüzey gitti, yerine bu geldi" diyor.
 */

type Errors = { name?: string; email?: string; message?: string };

/** Künye sırası: önce satıcının kim olduğu, sonra nasıl ulaşılacağı. */
const INFO = ['company', 'address', 'tax', 'email', 'phone', 'hours'] as const;
type InfoKey = (typeof INFO)[number];

/** Şirkete ait değerler çevrilmiyor (`lib/company.ts`); çalışma saati çeviriden. */
const COMPANY_INFO: Record<Exclude<InfoKey, 'hours'>, string> = {
  company: COMPANY.legalName,
  address: COMPANY.address,
  tax: `${COMPANY.taxOffice} / ${COMPANY.taxNumber}`,
  email: COMPANY.email,
  phone: COMPANY.phone,
};

const sceneV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base, delayChildren: 0.05 } },
};

const itemV: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/* Bölümdeki tek blur: sayfanın adı. */
const titleV: Variants = {
  hidden: { opacity: 0, y: 14, filter: 'blur(6px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: springFade(spring.default, dur.base),
  },
};

const infoV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight } },
};

const rowV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.snap) },
};

const formV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
  exit: { opacity: 0, y: -8, transition: { duration: dur.instant, ease: ease.in } },
};

const doneV: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default, dur.base) },
  exit: { opacity: 0, transition: { duration: dur.instant } },
};

export function ContactHero() {
  const t = useTranslations('contact');
  const locale = useLocale();
  const uid = useId();

  const nameId = `${uid}-name`;
  const emailId = `${uid}-email`;
  const companyId = `${uid}-company`;
  const messageId = `${uid}-message`;
  const potId = `${uid}-pot`;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [pot, setPot] = useState('');

  const [pending, setPending] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [alert, setAlert] = useState<string>();
  /* `null` = form duruyor. Sayı = gönderildi ve sunucu bu kadar saat içinde
     döneceğini söyledi (`DEMO_RESPONSE_HOURS`); rakam uydurulmuyor. */
  const [sentHours, setSentHours] = useState<number | null>(null);
  /* Hata gösterimi ilk gönderimden SONRA açılıyor: kullanıcı daha yazmaya
     başlamadan "adınızı yazın" demek formu suçlayıcı yapıyor. */
  const [tried, setTried] = useState(false);

  function validate(next = { name, email, message }): Errors {
    const out: Errors = {};
    if (next.name.trim().length < 2) out.name = t('form.errors.name');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(next.email.trim()))
      out.email = t('form.errors.email');
    /* Sunucu `message`i zorunlu tutmuyor (şemada `optional`), burada
       tutuluyor: mesajsız bir talep satış ekranında "bu kişi ne istiyor"
       sorusunu cevapsız bırakıyor. */
    if (next.message.trim().length < 10) out.message = t('form.errors.message');
    return out;
  }

  function onChange(field: keyof Errors, value: string) {
    if (field === 'name') setName(value);
    if (field === 'email') setEmail(value);
    if (field === 'message') setMessage(value);
    if (!tried) return;
    setErrors(validate({ name, email, message, [field]: value }));
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setTried(true);
    setAlert(undefined);

    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setPending(true);
    try {
      const ack = await api.post<DemoRequestAck>('/sales/demo-requests', {
        fullName: name.trim(),
        email: email.trim(),
        ...(company.trim() ? { companyName: company.trim() } : {}),
        message: message.trim(),
        // Satış ekibinin hangi dilde döneceği — formun doldurulduğu dil (13 dil,
        // `demoRequestInputSchema.locale`).
        locale: toLocale(locale),
        /* "Nereden geldi" — sunucu doğrulamıyor, olduğu gibi saklıyor.
           Gönderim ANINDA okunuyor: render sırasında `useSearchParams`
           çağırmak sayfayı istemci tarafına çekerdi. */
        source:
          typeof window === 'undefined'
            ? undefined
            : `${window.location.pathname}${window.location.search}`.slice(0, 200),
        company_website: pot,
      });
      setSentHours(ack.responseHours);
    } catch (error) {
      /* 429 ayrı bir cümle hak ediyor: "gönderilemedi" diyen genel metin, bir
         dakika bekleyip tekrar denemesi gereken kullanıcıyı formu yeniden
         doldurmaya itiyor. */
      setAlert(
        error instanceof ApiError && error.status === 429
          ? t('form.errors.tooMany')
          : t('form.errors.generic'),
      );
    } finally {
      setPending(false);
    }
  }

  function reset() {
    setSentHours(null);
    setTried(false);
    setErrors({});
    setAlert(undefined);
    setName('');
    setEmail('');
    setCompany('');
    setMessage('');
  }

  /** Künye satırı: e-posta ve telefon tıklanabilir, diğerleri düz metin. */
  function infoValue(key: InfoKey, value: string) {
    if (key === 'email') {
      return (
        <a className="reach-info-link" href={`mailto:${value}`}>
          {value}
        </a>
      );
    }
    if (key === 'phone') {
      return (
        <a className="reach-info-link" href={companyPhoneHref}>
          {value}
        </a>
      );
    }
    return value;
  }

  return (
    <section className="reach" aria-labelledby={`${uid}-title`}>
      {/* Header `position: fixed` — akışta yer kaplamıyor, payı burada.
          Ayrık beyaz kabuk header kutusunun altına taşıyor, bu yüzden pay
          `--header-h` değil kabuğun alt kenarından hesaplanıyor
          (`.page-head-space` ile aynı değer). */}
      <div className="reach-space" aria-hidden="true" />

      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div className="reach-frame" variants={sceneV} initial="hidden" animate="show">
            <div className="reach-layout">
              {/* --- Sol: başlık + iletişim bilgileri --- */}
              <div className="reach-copy">
                <motion.p className="reach-eyebrow" variants={itemV}>
                  {t('hero.eyebrow')}
                </motion.p>

                <motion.h1 className="reach-title" id={`${uid}-title`} variants={titleV}>
                  {t.rich('hero.title', { hl })}
                </motion.h1>

                <motion.p className="reach-lead" variants={itemV}>
                  {t('hero.lead')}
                </motion.p>

                {/* `<dl>` çünkü ad–değer çiftleri. Doğrudan çocuğu yalnız
                    `<div>`, `<dt>`, `<dd>` olabilir — satır sarmalayıcıları
                    bu yüzden div. */}
                <motion.dl className="reach-info" variants={infoV}>
                  {INFO.map((key) => {
                    const value =
                      key === 'hours' ? t('hero.info.hours.value') : COMPANY_INFO[key];
                    /* Boş değer BASILMIYOR — bugün yalnız çalışma saati
                       çeviriden geliyor ve o boş bırakılabilir. */
                    if (!value) return null;
                    return (
                      <motion.div className="reach-info-row" key={key} variants={rowV}>
                        <dt className="reach-info-label">{t(`hero.info.${key}.label`)}</dt>
                        <dd className="reach-info-value">{infoValue(key, value)}</dd>
                      </motion.div>
                    );
                  })}
                </motion.dl>
              </div>

              {/* --- Sağ: form --- */}
              <motion.div className="reach-panel" variants={itemV}>
                <h2 className="reach-panel-title">{t('form.title')}</h2>

                <AnimatePresence mode="wait" initial={false}>
                  {sentHours === null ? (
                    <motion.form
                      key="form"
                      className="reach-form"
                      onSubmit={onSubmit}
                      noValidate
                      variants={formV}
                      initial={false}
                      animate="show"
                      exit="exit"
                    >
                      <div className="reach-field">
                        <label className="auth-label" htmlFor={nameId}>
                          {t('form.name.label')} *
                        </label>
                        <AuthField
                          invalid={Boolean(errors.name)}
                        >
                          <input
                            className="auth-input"
                            id={nameId}
                            name="name"
                            type="text"
                            autoComplete="name"
                            placeholder={t('form.name.placeholder')}
                            value={name}
                            onChange={(e) => onChange('name', e.target.value)}
                            aria-invalid={Boolean(errors.name)}
                            aria-describedby={errors.name ? `${nameId}-error` : undefined}
                          />
                        </AuthField>
                        <FieldError id={`${nameId}-error`} message={errors.name} />
                      </div>

                      <div className="reach-field">
                        <label className="auth-label" htmlFor={emailId}>
                          {t('form.email.label')} *
                        </label>
                        <AuthField
                          invalid={Boolean(errors.email)}
                        >
                          <input
                            className="auth-input"
                            id={emailId}
                            name="email"
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            placeholder={t('form.email.placeholder')}
                            value={email}
                            onChange={(e) => onChange('email', e.target.value)}
                            aria-invalid={Boolean(errors.email)}
                            aria-describedby={errors.email ? `${emailId}-error` : undefined}
                          />
                        </AuthField>
                        <FieldError id={`${emailId}-error`} message={errors.email} />
                      </div>

                      <div className="reach-field">
                        <label className="auth-label" htmlFor={companyId}>
                          {t('form.company.label')}{' '}
                          <span className="reach-optional">{t('form.optional')}</span>
                        </label>
                        <AuthField
                          invalid={false}
                        >
                          <input
                            className="auth-input"
                            id={companyId}
                            name="organization"
                            type="text"
                            autoComplete="organization"
                            placeholder={t('form.company.placeholder')}
                            value={company}
                            onChange={(e) => setCompany(e.target.value)}
                          />
                        </AuthField>
                      </div>

                      <div className="reach-field reach-field--area">
                        <label className="auth-label" htmlFor={messageId}>
                          {t('form.message.label')} *
                        </label>
                        <AuthField
                          invalid={Boolean(errors.message)}
                        >
                          <textarea
                            className="auth-input reach-textarea"
                            id={messageId}
                            name="message"
                            rows={5}
                            maxLength={2000}
                            placeholder={t('form.message.placeholder')}
                            value={message}
                            onChange={(e) => onChange('message', e.target.value)}
                            aria-invalid={Boolean(errors.message)}
                            aria-describedby={errors.message ? `${messageId}-error` : undefined}
                          />
                        </AuthField>
                        <FieldError id={`${messageId}-error`} message={errors.message} />
                      </div>

                      {/* 🔴 Bal küpü — ekran dışında ama DOM'da. */}
                      <div className="reach-pot" aria-hidden="true">
                        <label htmlFor={potId}>{t('form.honeypot')}</label>
                        <input
                          id={potId}
                          name="company_website"
                          type="text"
                          tabIndex={-1}
                          autoComplete="off"
                          value={pot}
                          onChange={(e) => setPot(e.target.value)}
                        />
                      </div>

                      <FormAlert message={alert} />

                      <SubmitButton
                        pending={pending}
                        label={t('form.submit')}
                        pendingLabel={t('form.sending')}
                      />
                    </motion.form>
                  ) : (
                    <motion.div
                      key="done"
                      className="reach-done"
                      role="status"
                      variants={doneV}
                      initial="hidden"
                      animate="show"
                      exit="exit"
                    >
                      <span className="reach-done-mark" aria-hidden="true">
                        <RiCheckLine size={22} />
                      </span>
                      <h3 className="reach-done-title">{t('form.success.title')}</h3>
                      <p className="reach-done-text">
                        {t('form.success.body', { hours: sentHours })}
                      </p>
                      <button type="button" className="reach-again" onClick={reset}>
                        {t('form.success.again')}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
